import React from 'react';
import Isvg from 'react-inlinesvg';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useFormContext, useWatch } from 'react-hook-form';

import { ComboboxEmpty, ComboboxFooter, ComboboxInput, ComboboxItem, ComboboxList } from 'components/Combobox';
import { Combobox, Label } from 'components/HookForm';
import Message from 'components/Message';
import { SvgArrow } from 'components/Svgs';
import When from 'components/When';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';

import { rollUpSelection, selectionAfterRemoval } from '../Modifiers.itemSelection';
import ModifierSelectionRows from './ModifierSelectionRows';
import messages from './ModifierItemPicker.messages';
import './ModifierItemPicker.scss';

const { block, element } = bem('modifier-item-picker');

const itemShape = PropTypes.shape({
  categoryName: PropTypes.string,
  id: PropTypes.string.isRequired,
  menuId: PropTypes.string,
  menuName: PropTypes.string,
  name: PropTypes.string,
});

const ModifierItemPicker = ({ name, items, disabled }) => {
  const intl = useIntl();
  const { control, setValue } = useFormContext();
  const selectedIds = useWatch({ control, name }) || [];

  const rows = rollUpSelection(items, selectedIds);
  const allSelected = items.length > 0 && items.every(item => selectedIds.includes(item.id));

  const changeSelection = nextIds =>
    setValue(name, nextIds, { shouldDirty: true, shouldTouch: true, shouldValidate: true });

  return (
    <div className={block()}>
      {disabled ? (
        <Label message={messages.itemsLabel} />
      ) : (
        <Combobox
          className={element('combobox')}
          name={name}
          label={messages.selectItemsLabel}
          required={messages.itemsRequired}
          showRequiredLabel
          multiple>
          <ComboboxInput
            placeholder={intl.formatMessage(messages.searchPlaceholder)}
            prefix={<Isvg src={ICONS.searchGlassOutline} className={element('search-icon')} />}
            suffix={<SvgArrow direction="down" />}
          />
          <ComboboxList>
            {items.map(item => (
              <ComboboxItem key={item.id} value={item.id} label={item.name}>
                <div className={element('option')}>
                  <div className={element('option-text')}>
                    <span className={element('option-name')}>{item.name}</span>
                    <span className={element('option-detail')}>{`${item.categoryName} · ${item.menuName}`}</span>
                  </div>
                  <When predicate={selectedIds.includes(item.id)}>
                    <Isvg src={ICONS.checkmark} className={element('option-check')} />
                  </When>
                </div>
              </ComboboxItem>
            ))}
            <ComboboxFooter>
              <label className={element('select-all')}>
                <input
                  type="checkbox"
                  className={element('select-all-box')}
                  checked={allSelected}
                  onChange={() => changeSelection(allSelected ? [] : items.map(item => item.id))}
                />
                <Message message={messages.selectAll} values={{ count: items.length }} />
              </label>
            </ComboboxFooter>
          </ComboboxList>
          <ComboboxEmpty>
            <Message message={items.length === 0 ? messages.noItems : messages.noResults} />
          </ComboboxEmpty>
        </Combobox>
      )}

      {rows.length > 0 ? (
        <ModifierSelectionRows
          rows={rows}
          disabled={disabled}
          onRemove={row => changeSelection(selectionAfterRemoval(selectedIds, row))}
        />
      ) : (
        <p className={element('empty')}>
          <Message message={messages.noItemsSelected} />
        </p>
      )}
    </div>
  );
};

ModifierItemPicker.propTypes = {
  disabled: PropTypes.bool,
  items: PropTypes.arrayOf(itemShape),
  name: PropTypes.string.isRequired,
};

ModifierItemPicker.defaultProps = {
  items: [],
};

export default ModifierItemPicker;
